/**
 * Settings → Leave Policy → Time in lieu (DESIGN-toil.md screen 1). Saves on its own
 * (PUT /toil/settings), so it never rides along with — or nests inside — the page's form.
 *
 * I   off until switched on; switching on creates the Time in lieu leave type.
 * E2  the usual rate, ticked for the approver.
 * F   use within N months of the day worked; Never by default.
 * 6   switching off deletes nothing.
 */
import React, { useEffect, useState } from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toilApi } from '../../lib/api';

const RATES = [['1', 'Straight time ×1'], ['1.5', 'Time and a half ×1.5'], ['2', 'Double time ×2']];
const MONTHS = [[0, 'Never'], [1, '1 month'], [2, '2 months'], [3, '3 months'], [6, '6 months'], [12, '12 months']];

function Seg({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex flex-wrap rounded-full border border-border overflow-hidden" data-testid={testid}>
      {options.map(([v, label]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={`px-3 py-1.5 text-xs font-semibold ${String(value) === String(v) ? 'bg-foreground text-background' : 'bg-background'}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

export default function ToilSettingsCard() {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    toilApi.getSettings().then((r) => {
      const v = { enabled: !!r.data.enabled, default_rate: r.data.default_rate || '1',
        expiry_months: r.data.expiry_months || 0, expiry_emails: r.data.expiry_emails !== false };
      setS(v); setSaved(v);
    }).catch(() => {});
  }, []);
  if (!s) return null;
  const dirty = JSON.stringify(s) !== JSON.stringify(saved);

  const save = async () => {
    setSaving(true);
    try {
      const r = await toilApi.saveSettings({ ...s, expiry_months: s.expiry_months || null });
      const v = { enabled: !!r.data.enabled, default_rate: r.data.default_rate,
        expiry_months: r.data.expiry_months || 0, expiry_emails: r.data.expiry_emails !== false };
      setS(v); setSaved(v);
      toast.success('Time in lieu settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the time in lieu settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-hive p-6 space-y-4" data-testid="toil-settings">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-teal-500" /> Time in lieu (TOIL)</h3>
          <p className="text-sm text-muted-foreground mt-1">
            People claim extra time they've worked and take it as time off. It has its own balance, separate from holiday.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="toil-enabled" />
          {s.enabled ? 'On' : 'Off'}
        </label>
      </div>

      {s.enabled && (
        <>
          <div className="space-y-1.5">
            <p className="text-sm font-semibold">Usual rate <span className="font-normal text-muted-foreground">— ticked for the approver when they sign off a claim</span></p>
            <Seg options={RATES} value={s.default_rate} onChange={(v) => setS({ ...s, default_rate: v })} testid="toil-usual-rate" />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-semibold">Use within</p>
            <Seg options={MONTHS} value={s.expiry_months} onChange={(v) => setS({ ...s, expiry_months: v })} testid="toil-use-within" />
            <p className="text-xs text-muted-foreground">Counted from the day the extra time was worked. Changing this doesn't move dates already set.</p>
          </div>
          {s.expiry_months > 0 && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={s.expiry_emails} onChange={(e) => setS({ ...s, expiry_emails: e.target.checked })} className="accent-primary" />
              Email people 14 days before time in lieu runs out, and when any is lost
            </label>
          )}
        </>
      )}
      {!s.enabled && saved.enabled && (
        <p className="text-xs text-muted-foreground">Switching it off deletes nothing: balances and history stay, and claims already waiting can still be decided.</p>
      )}

      <div className="flex justify-end">
        <Button type="button" className="btn-primary" disabled={!dirty || saving} onClick={save} data-testid="toil-settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save time in lieu'}
        </Button>
      </div>
    </div>
  );
}
